import React from "react";
import classNames from "classnames";
import { isMobile } from "./util";

const polarToX = (angle, distance) => Math.cos(angle - Math.PI / 2) * distance;

const polarToY = (angle, distance) => Math.sin(angle - Math.PI / 2) * distance;

const points = points => {
  return points
    .map(point => point[0].toFixed(4) + "," + point[1].toFixed(4))
    .join(" ");
};

const axis = options => (col, i) => (
  <polyline
    key={`poly-axis-${i}`}
    points={points([
      [0, 0],
      [
        polarToX(col.angle, options.chartSize / 2),
        polarToY(col.angle, options.chartSize / 2),
      ],
    ])}
    {...options.axisProps(col)}
  />
);

const dot = (columns, options) => (chartData, i) => {
  const data = chartData.data;
  const meta = chartData.meta || {};
  const extraProps = options.dotProps(meta);

  let mouseEnter = () => {};
  let mouseLeave = () => {};

  if (extraProps.mouseEnter) {
    mouseEnter = extraProps.mouseEnter;
  }

  if (extraProps.mouseLeave) {
    mouseLeave = extraProps.mouseLeave;
  }

  return columns.map(col => {
    const val = data[col.key];
    if ("number" !== typeof val) {
      throw new Error(`Data set ${i} is invalid.`);
    }

    return (
      <circle
        key={`dot-${col.key}-${val}`}
        cx={polarToX(col.angle, (val * options.chartSize) / 2)}
        cy={polarToY(col.angle, (val * options.chartSize) / 2)}
        className={[extraProps.className, meta.class].join(" ")}
        onMouseEnter={() => mouseEnter({ key: col.key, value: val, idx: i })}
        onMouseLeave={() => mouseLeave({})}
      />
    );
  });
};

const shape = (columns, options) => (item, i) => {
  const data = item.data;
  const meta = item.meta || {};
  const extraProps = options.shapeProps(meta);

  return (
    <React.Fragment key={`shape-${i}`}>
      <clipPath id={`shape-${i}`}>
        <path
          d={options.smoothing(
            columns.map(col => {
              const val = data[col.key];

              if ("number" !== typeof val) {
                throw new Error(`Data set ${i} is invalid.`);
              }

              return [
                polarToX(col.angle, (val * options.chartSize) / 2), // point position x
                polarToY(col.angle, (val * options.chartSize) / 2), // point position y
                polarToX(col.angle, ((val - 0.3) * options.chartSize) / 2), // control position x
                polarToY(col.angle, ((val - 0.3) * options.chartSize) / 2), // control position y
              ];
            })
          )}
          {...extraProps}
          className={classNames(extraProps.className, meta.class)}
        />
      </clipPath>

      <image
        clipPath={`url(#shape-${i})`}
        xlinkHref={meta.image}
        src={meta.image}
        alt=""
        width="100%"
        height="100%"
        x={(options.size / 2) * -1}
        y={(options.size / 2) * -1}
        preserveAspectRatio="xMinYMin slice"
        className={classNames("image", { active: item.active })}
      />
    </React.Fragment>
  );
};

const scale = (options, value) => (
  <circle
    key={`circle-${value}`}
    cx={0}
    cy={0}
    r={(value * options.chartSize) / 2}
    {...options.scaleProps(value)}
  />
);

const lineBreakHTML = (col, x, y) => {
  const { caption: text } = col;
  const words = text.split(" ");
  const element = document.createElement("text");

  document.body.appendChild(element);
  element.innerHTML = '<tspan id="testElement"></tspan>';

  const testElement = element.querySelector("#testElement");
  const maxWidth = 200;

  const offsetY = 16;
  const offsetX = 16;

  let elements = [];
  let line = "";
  let testLine = "";
  let metrics;
  let width;

  for (let i = 0; i < words.length; i++) {
    testLine = (line + " " + words[i]).trim();
    testElement.innerHTML = testLine;

    metrics = testElement.getBoundingClientRect();
    width = metrics.width;

    if (width >= maxWidth) {
      elements.push({
        x: Number(x),
        dy: 0,
        line,
      });

      testLine = "";
      line = "";
      testElement.innerHTML = "";
    } else {
      line = testLine;
    }
  }

  elements.push({
    x: Number(x),
    dy: 0,
    line,
  });

  if (elements.length > 1) {
    elements = elements.map((elem, i) => {
      if (i === 0) {
        elem.dy = offsetY / -2;
        return elem;
      }

      elem.x += offsetX;
      elem.dy = offsetY;
      return elem;
    });
  }

  elements = elements.reduce((acc, curr) => {
    return acc.concat(
      `<tspan x="${curr.x}" dy="${curr.dy}">${curr.line}</tspan>`
    );
  }, "");

  document.body.removeChild(element);
  return elements;
};

const caption = (options, slug) => (col, i, arr) => {
  const extraProps = options.captionProps(col);

  let {
    mouseEnter,
    mouseLeave,
    click,
    children,
    className,
    ...rest
  } = extraProps;

  const defaultFill = "#000";
  const fill = slug === col.key ? col.meta.color : defaultFill;

  const activeRadius = 6;
  const defaultRadius = 3;
  const r = slug === col.key ? activeRadius : defaultRadius;

  mouseEnter = mouseEnter ? mouseEnter : () => {};
  mouseLeave = mouseLeave ? mouseLeave : () => {};
  click = click ? click : () => {};

  const len = arr.length;
  const x = polarToX(col.angle, (options.size / 2) * 0.95).toFixed(4);
  const y = polarToY(col.angle, (options.size / 2) * 0.95).toFixed(4);

  let textAnchor = "end";
  let dx = options.captionProps(col).fontSize || 20;
  let rotation = (360 / len) * i + 90;

  // Flip text if it's being rendered upside down on the right-hand side of the
  // chart
  if (rotation > 90 && rotation < 270) {
    textAnchor = "start";
    dx *= -1;
    rotation += 180;
  }

  return isMobile() ? (
    <circle
      transform={`rotate(${rotation},${x},${y})`}
      key={`caption-of-${col.key}`}
      cx={x}
      cy={y}
      r={r}
      fill={fill}
    />
  ) : (
    <text
      transform={`rotate(${rotation},${x},${y})`}
      key={`caption-of-${col.key}`}
      x={x}
      y={y}
      dx={dx}
      onMouseEnter={() => mouseEnter({ key: col.key, idx: i })}
      onMouseLeave={() => mouseLeave({})}
      onClick={e => click({ e, key: col.key })}
      dangerouslySetInnerHTML={{ __html: lineBreakHTML(col, x, y) }}
      className={classNames(className, { active: col.active })}
      textAnchor={textAnchor}
      {...rest}
    />
  );
};

const render = (captions, chartData, options = {}, slug) => {
  if ("object" !== typeof captions || Array.isArray(captions)) {
    throw new Error("captions must be an object");
  }

  if (!Array.isArray(chartData)) {
    throw new Error("data must be an array");
  }

  options.chartSize = options.size / options.zoomDistance;

  const columns = Object.keys(captions).map((key, i, all) => {
    const entry = chartData.find(node => node.slug === key);
    const { meta, active } = entry;

    return {
      key,
      meta,
      active,
      caption: captions[key],
      angle: (Math.PI * 2 * i) / all.length,
    };
  });

  const groups = [
    <g key={`g-groups}`}>{chartData.map(shape(columns, options))}</g>,
  ];

  if (options.captions) {
    groups.push(
      <g key={`poly-captions`}>{columns.map(caption(options, slug))}</g>
    );
  }

  if (options.dots) {
    groups.push(<g key={`g-dots`}>{chartData.map(dot(columns, options))}</g>);
  }

  if (options.axes) {
    groups.unshift(<g key={`group-axes`}>{columns.map(axis(options))}</g>);
  }

  if (options.scales > 0) {
    const scales = [];
    for (let i = options.scales; i > 0; i--) {
      scales.push(scale(options, i / options.scales));
    }
    groups.unshift(<g key={`poly-scales`}>{scales}</g>);
  }

  const delta = (options.size / 2).toFixed(4);
  return <g transform={`translate(${delta},${delta})`}>{groups}</g>;
};

export default render;
